import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Plus, Building2, Loader2, Check, X, Edit2, Trash2, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getBrands, createBrand, updateBrand, deleteBrand } from '../../../api/socialMedia';

/**
 * Brand Switcher Component
 * Allows users to switch between brands and manage them (create, edit, delete)
 * Like Postiz, users can organize their social accounts into separate brands/workspaces
 */
const BrandSwitcher = ({
    currentBrandId,
    onBrandChange,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [brands, setBrands] = useState([]);
    const [brandLimit, setBrandLimit] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create brand state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');

    // Edit brand state
    const [editingBrandId, setEditingBrandId] = useState(null);
    const [editBrandName, setEditBrandName] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    // Delete confirmation state
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    // Find current brand
    const currentBrand = brands.find(b => b.id === currentBrandId);

    // Update dropdown position when opened
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                left: rect.left,
                width: Math.max(rect.width, 300),
            });
        }
    }, [isOpen]);

    // Load brands
    useEffect(() => {
        loadBrands();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both the button and any dropdown content
            const isOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
            const isOutsideDropdown = !event.target.closest('[data-brand-dropdown]');

            if (isOpen && isOutsideButton && isOutsideDropdown) {
                setIsOpen(false);
                setShowCreateForm(false);
                setEditingBrandId(null);
                setConfirmDelete(null);
                setNewBrandName('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const loadBrands = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('[BrandSwitcher] Loading brands...');
            const response = await getBrands();
            console.log('[BrandSwitcher] Brands response:', response);
            console.log('[BrandSwitcher] Brand limit from API:', response.limit, '(0 = unlimited)');

            setBrands(response.brands || []);
            // Use nullish coalescing - limit of 0 means unlimited, don't default to 1
            const limit = response.limit ?? 0;
            console.log('[BrandSwitcher] Setting brandLimit to:', limit);
            setBrandLimit(limit);

            // If no current brand selected and brands exist, select the first one
            if (!currentBrandId && response.brands?.length > 0) {
                console.log('[BrandSwitcher] Auto-selecting first brand:', response.brands[0].id);
                onBrandChange(response.brands[0].id);
            }
        } catch (err) {
            console.error('[BrandSwitcher] Failed to load brands:', err);
            setError('Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBrand = async (e) => {
        e.preventDefault();
        if (!newBrandName.trim()) return;

        try {
            setCreateLoading(true);
            const newBrand = await createBrand({ name: newBrandName.trim() });
            setBrands(prev => [...prev, newBrand]);
            setNewBrandName('');
            setShowCreateForm(false);
            onBrandChange(newBrand.id);
            setIsOpen(false);
        } catch (err) {
            console.error('Failed to create brand:', err);
            setError(err.message || 'Failed to create brand');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditBrand = async (e) => {
        e.preventDefault();
        if (!editBrandName.trim() || !editingBrandId) return;

        try {
            setEditLoading(true);
            const updatedBrand = await updateBrand(editingBrandId, { name: editBrandName.trim() });
            setBrands(prev => prev.map(b => b.id === editingBrandId ? updatedBrand : b));
            setEditingBrandId(null);
            setEditBrandName('');
        } catch (err) {
            console.error('Failed to update brand:', err);
            setError(err.message || 'Failed to update brand');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteBrand = async () => {
        if (!confirmDelete) return;

        try {
            setDeleteLoading(true);
            await deleteBrand(confirmDelete);
            setBrands(prev => prev.filter(b => b.id !== confirmDelete));

            // If deleted brand was selected, switch to another
            if (currentBrandId === confirmDelete) {
                const remainingBrands = brands.filter(b => b.id !== confirmDelete);
                onBrandChange(remainingBrands[0]?.id || null);
            }

            setConfirmDelete(null);
        } catch (err) {
            console.error('Failed to delete brand:', err);
            setError(err.message || 'Failed to delete brand');
        } finally {
            setDeleteLoading(false);
        }
    };

    const canAddMore = brandLimit === 0 || brands.length < brandLimit;

    if (loading) {
        return (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur border border-white/20">
                <Loader2 className="w-4 h-4 animate-spin text-white/70" />
                <span className="text-sm text-white/70">Loading...</span>
            </div>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button - Always shown */}
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${
                    isOpen
                        ? 'border-white/40 bg-white/20'
                        : 'border-white/20 bg-white/10 hover:bg-white/15 hover:border-white/30'
                } backdrop-blur ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {/* Brand Logo/Icon */}
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                    {currentBrand?.logoUrl ? (
                        <img
                            src={currentBrand.logoUrl}
                            alt={currentBrand.name}
                            className="w-full h-full rounded-lg object-cover"
                        />
                    ) : (
                        <Building2 className="w-4 h-4 text-white" />
                    )}
                </div>

                {/* Brand Name */}
                <div className="flex-1 text-left min-w-0">
                    <p className="font-medium text-white truncate">
                        {currentBrand?.name || (brands.length === 0 ? 'Create a Brand' : 'Select Brand')}
                    </p>
                </div>

                <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown - Rendered in Portal */}
            {isOpen && ReactDOM.createPortal(
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            zIndex: 9999,
                        }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden"
                        data-brand-dropdown="true"
                    >
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Settings className="w-4 h-4 text-slate-500" />
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Manage Brands
                                    </span>
                                </div>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {brands.length} {brandLimit === 0 ? '(unlimited)' : `/ ${brandLimit}`} brands
                                </span>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400 border-b border-red-100 dark:border-red-900/30">
                                {error}
                                <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600">×</button>
                            </div>
                        )}

                        {/* Brand List */}
                        <div className="max-h-64 overflow-y-auto">
                            {brands.length === 0 ? (
                                <div className="px-4 py-6 text-center">
                                    <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                                        No brands yet
                                    </p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500">
                                        Create a brand to organize your social accounts
                                    </p>
                                </div>
                            ) : (
                                <div className="py-1">
                                    {brands.map((brand) => (
                                        <div key={brand.id} className="px-2">
                                            {editingBrandId === brand.id ? (
                                                /* Edit Form */
                                                <form onSubmit={handleEditBrand} className="flex items-center gap-2 p-2">
                                                    <input
                                                        type="text"
                                                        value={editBrandName}
                                                        onChange={(e) => setEditBrandName(e.target.value)}
                                                        autoFocus
                                                        className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={editLoading || !editBrandName.trim()}
                                                        className="p-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setEditingBrandId(null); setEditBrandName(''); }}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </form>
                                            ) : confirmDelete === brand.id ? (
                                                /* Delete Confirmation */
                                                <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg m-1">
                                                    <span className="flex-1 text-sm text-red-600 dark:text-red-400">Delete?</span>
                                                    <button
                                                        onClick={handleDeleteBrand}
                                                        disabled={deleteLoading}
                                                        className="p-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmDelete(null)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                /* Brand Item */
                                                <div
                                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer group transition-colors ${
                                                        currentBrandId === brand.id
                                                            ? 'bg-teal-50 dark:bg-teal-900/20'
                                                            : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                                    }`}
                                                >
                                                    <div
                                                        onClick={() => {
                                                            onBrandChange(brand.id);
                                                            setIsOpen(false);
                                                        }}
                                                        className="flex items-center gap-3 flex-1 min-w-0"
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                                                            {brand.logoUrl ? (
                                                                <img src={brand.logoUrl} alt={brand.name} className="w-full h-full rounded-lg object-cover" />
                                                            ) : (
                                                                <Building2 className="w-4 h-4 text-white" />
                                                            )}
                                                        </div>
                                                        <span className={`font-medium truncate ${
                                                            currentBrandId === brand.id
                                                                ? 'text-teal-700 dark:text-teal-400'
                                                                : 'text-slate-700 dark:text-slate-300'
                                                        }`}>
                                                            {brand.name}
                                                        </span>
                                                        {currentBrandId === brand.id && (
                                                            <Check className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                                                        )}
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingBrandId(brand.id);
                                                                setEditBrandName(brand.name);
                                                            }}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setConfirmDelete(brand.id);
                                                            }}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Add Brand Section */}
                        <div className="border-t border-slate-100 dark:border-slate-700 p-2">
                            {showCreateForm ? (
                                <form onSubmit={handleCreateBrand} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={newBrandName}
                                        onChange={(e) => setNewBrandName(e.target.value)}
                                        placeholder="Enter brand name..."
                                        autoFocus
                                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder-slate-400"
                                    />
                                    <button
                                        type="submit"
                                        disabled={createLoading || !newBrandName.trim()}
                                        className="p-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
                                    >
                                        {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setShowCreateForm(false); setNewBrandName(''); }}
                                        className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </form>
                            ) : canAddMore ? (
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add New Brand
                                </button>
                            ) : (
                                <p className="text-xs text-center text-amber-600 dark:text-amber-400 py-2">
                                    Brand limit reached. Upgrade to add more.
                                </p>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default BrandSwitcher;
